import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lock, Clock, ChevronRight } from "lucide-react";
import { Progress } from "../components/ui/progress";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const STATUS = {
  completed: { label: "Completed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: Check },
  in_progress: { label: "In Progress", cls: "border-amber-200 bg-amber-50 text-amber-700", icon: Clock },
  locked: { label: "Locked", cls: "border-slate-200 bg-slate-100 text-slate-500", icon: Lock },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = () => api.get("/onboarding").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const completed = items.filter((i) => i.status === "completed").length;
  const pct = items.length ? Math.round((completed / items.length) * 100) : 0;

  const open = (item) => { if (item.route) navigate(item.route); };

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Overall progress</h2>
            <p className="text-sm text-slate-500">{completed} of {items.length} steps complete</p>
          </div>
          <div className="text-2xl font-extrabold text-violet-600" data-testid="onboarding-percent">{pct}%</div>
        </div>
        <Progress value={pct} className="mt-4 h-2" />
      </Card>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Onboarding checklist</h2>
        <p className="mt-1 text-xs text-slate-500">Each step completes automatically once the system verifies you've done it. Click a step to open the activity.</p>
        <div className="mt-4 space-y-3">
          {items.map((item) => {
            const s = STATUS[item.status] || STATUS.locked;
            const Icon = s.icon;
            return (
              <button
                key={item.n}
                type="button"
                onClick={() => open(item)}
                disabled={!item.route}
                data-testid={`checklist-item-${item.n}`}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-100 p-4 text-left transition-colors hover:border-violet-200 hover:bg-violet-50/40 disabled:cursor-default disabled:hover:border-slate-100 disabled:hover:bg-transparent"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${item.status === "completed" ? "bg-emerald-500 text-white" : item.status === "in_progress" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                    {item.status === "completed" ? <Check className="h-4 w-4" /> : item.status === "locked" ? <Lock className="h-4 w-4" /> : item.n}
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">{item.title}</div>
                    <div className="text-xs text-slate-500">{item.status === "completed" ? item.desc : (item.hint || item.desc)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`checklist-status-${item.n}`}>
                    <Icon className="h-3 w-3" /> {s.label}
                  </span>
                  {item.overridden && <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Admin verified</span>}
                  {item.route && <ChevronRight className="h-4 w-4 text-slate-300" />}
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
